import { NavigationFailure, RouteLocationNormalized, RouteLocationRaw, Router as VueRouter } from "vue-router";
import { default as vueRouter } from "../../../router";
import { AuthStoreService } from "../security/auth-store.service";
import { UsersResource } from "@/core/features/users/users.resource";
import { get } from "lodash";
import { UserDto } from "@/core/features/users/user.dto";
import { SecurityManager } from "@/core/common/security/security.manager";
import { UnauthorizedError } from "@/core/common/http/errors/unauthorized.error";
import { PostsResource } from "@/core/features/posts/posts.resource";
import { usePostHog } from "@/composables/posthog.composable";
import { PosthogEventType } from "@/types/PosthogEventType";
import { canAccessPlanningBoard } from "@/modules/scheduler/planning-board/can-access-planning-board";
import {
  findSectionRootRoute,
  getSectionFirstAllowedRoute,
  isSameResolvedPath,
} from "@/core/common/router/section-first-allowed-route";

export class Router {
  private readonly router: VueRouter;
  private readonly posthog;
  private readonly identifyUser;
  private readonly startRecording;
  private readonly stopRecording;

  constructor(
    private readonly authStoreService: AuthStoreService,
    private readonly securityManager: SecurityManager,
    private readonly usersResource: UsersResource,
    private readonly postsResource: PostsResource
  ) {
    this.router = vueRouter;
    const { posthog, identifyUser, startRecording, stopRecording } = usePostHog();
    this.posthog = posthog;
    this.identifyUser = identifyUser;
    this.startRecording = startRecording;
    this.stopRecording = stopRecording;
    this.checkIfRequiresAuth();
    this.setupAnalytics();
  }

  push(url: string): Promise<void | NavigationFailure> {
    return this.router.push(url);
  }

  replace(url: string): Promise<void | NavigationFailure> {
    return this.router.replace(url);
  }

  getInstance(): VueRouter {
    return this.router;
  }

  identifyPosthogUser(user: UserDto): void {
    if (this.identifyUser) {
      this.identifyUser(user);
    }
  }

  startPosthogRecording(): void {
    if (this.startRecording) {
      this.startRecording();
    }
  }

  stopPosthogRecording(): void {
    if (this.stopRecording) {
      this.stopRecording();
    }
  }

  capturePosthogEvent(eventName: PosthogEventType, properties?: Record<string, any>): void {
    if (this.posthog) {
      this.posthog.capture(eventName, properties);
    }
  }

  private checkIfRequiresAuth() {
    this.router.beforeEach(async (to) => {
      if (to.meta?.requiresAuth) {
        if (!(await this.isLogged())) {
          new Promise(() => {
            throw new UnauthorizedError("No tienes permisos para visitar esta página", to.fullPath, to);
          });
        } else {
          if (to.meta?.allowExpiredPassword) {
            return;
          }

          const me = await this.getMe();
          const redirection = await this.checkIfRoleHasPermissions(to, me);
          if (redirection) return redirection;
          if (await this.hasUnreadedPosts(to, me)) {
            return {
              path: "/my-space/posts",
              query: { unreadWarning: "true" },
            };
          }
        }
      }
    });
  }

  private async hasUnreadedPosts(to: RouteLocationNormalized, me: UserDto) {
    if (to.path.includes("posts") || me.username === "autotest") return 0;
    try {
      const unreadedPost = await this.postsResource.getUnread();
      return unreadedPost > 0;
    } catch {
      return false;
    }
  }

  private async getMe(): Promise<UserDto> {
    try {
      return await this.usersResource.getMe();
    } catch (e) {
      return null;
    }
  }

  private async checkIfRoleHasPermissions(to: RouteLocationNormalized, me?: UserDto) {
    if (!me) {
      return {
        path: "/login",
        query: { redirects: to.meta.requiresAuth ? to.fullPath : undefined },
      };
    }

    if (to.meta?.sectionAccess && !this.securityManager.hasSectionAccess(to.meta.sectionAccess, me)) {
      return { path: "/403" };
    }

    const requiredPermissions = get(to, "meta.permissions", []);
    if (!this.securityManager.hasPermission(requiredPermissions, me)) {
      const sectionFallbackRoute = this.getSectionFallbackRoute(to, me);
      if (sectionFallbackRoute) {
        return sectionFallbackRoute;
      }

      return { path: "/403" };
    }

    if (get(to, "meta.requiresPlanningBoardBusinessUnit") && !canAccessPlanningBoard(me)) {
      return { path: "/403" };
    }

    const sectionRootRoute = findSectionRootRoute(to.matched);
    if (sectionRootRoute && sectionRootRoute.path === to.path) {
      const redirection = getSectionFirstAllowedRoute(this.router, this.securityManager, sectionRootRoute, me);
      if (redirection) {
        return redirection;
      }

      return { path: "/403" };
    }
  }

  private getSectionFallbackRoute(to: RouteLocationNormalized, me: UserDto): RouteLocationRaw | null {
    const sectionRootRoute = findSectionRootRoute(to.matched);
    if (!sectionRootRoute) {
      return null;
    }

    const redirection = getSectionFirstAllowedRoute(this.router, this.securityManager, sectionRootRoute, me);
    if (!redirection || isSameResolvedPath(this.router, to.path, redirection)) {
      return null;
    }

    return redirection;
  }

  private async isLogged(): Promise<boolean> {
    return this.authStoreService.hasToken();
  }

  private setupAnalytics(): void {
    if (!this.posthog) return;

    this.router.afterEach((to) => {
      this.posthog.capture("$pageview", {
        $current_url: to.fullPath,
      });
    });
  }
}
